<template>
	<v-card v-show="!isSubpageOpen" :class="cardClass()">
		<v-card-text class="flex-grow-0 flex-shrink-0">
			<div class="header">
				<div>
					<h2><span class="action-header-photo">Take</span> Photos Now</h2>
				</div>
			</div>
			<v-form ref="form" v-model="form" :autocomplete="autocomplete">
				<CustomerAutoComplete
					v-model="selectedCustomer"
					label="Contact (Optional)"
					density="compact"
					@update:modelValue="setCustomer" />

				<!-- <v-text-field
					:autocomplete="autocomplete"
					v-model="contactName"
					ref="name"
					hide-details
					label="Contact"
					variant="outlined"
					density="compact"
					clearable
					:maxLength="56"
					class="field-border-radius"
					:class="allowReset ? 'reset' : ''" /> -->

				<ProjectAutoComplete
					:model-value="newSelectedProject"
					:disabled="disableProjectField"
					:entity="null"
					density="compact"
					label="Project (Optional)"
					@update:modelValue="updateProject" />
				<AddressLookupLink v-if="!disableProjectField" @openAddressLookup="openAddressLookup" />

				<!-- LSP-20406: tag the session as part of starting the action. -->
				<SessionTagPicker v-model="selectedSessionTags" />
				<SmartPackSelector
					v-model="scribePackSelection"
					ref="smartPackSelector"
					@update:model-value="scribePackSelectionTouched = true"
					@update:processing="updateScribePackProcessing"
					@update:user-input-pending="scribeUserInputPending = $event" />
				<v-alert
					v-if="scribeUserInputPending"
					type="info"
					variant="tonal"
					density="compact"
					class="mt-3 cursor-pointer"
					@click="$refs.smartPackSelector?.reopenDialog()">
					<div class="text-body-2">This Spark report requires additional input. Click here to provide the required details.</div>
				</v-alert>
				<ScheduledSparkNotice v-if="scribePackSelection" :delay-seconds="contentNotificationDelay" />
				<InstructionPicker
					v-model="instructionId"
					class="mt-2"
					:create-return-to="instructionCreateReturnTo"
					create-return-action="photo"
					:create-draft-key="instructionReturn.storageKey()"
					:preselect-default="instructionPreselectAllowed"
					@preselect-settled="instructionPreselectAllowed = false"
					@create-start="saveInstructionCreateDraft" />
			</v-form>

			<div class="recording-disclaimer">Click “Take Photos” and a new tab will open with your camera ready to go.</div>
		</v-card-text>

		<v-card-actions class="d-flex justify-space-between pa-2 ga-2">
			<!--Close-->
			<v-btn
				variant="outlined"
				rounded="xl"
				size="x-large"
				style="flex: 1"
				elevation="0"
				@click.stop.prevent="close"
				@touchstart.stop.prevent="close()">
				Cancel
			</v-btn>

			<!--Start-->
			<PrimaryBtn style="flex: 1" size="x-large" :disabled="startButtonDisabled()" :loading="starting" @click="start()">
				{{ dynamicActioningText }}
			</PrimaryBtn>
		</v-card-actions>
	</v-card>
	<AddressLookup v-if="isAddressLookupOpen" @projectSelected="updateProject" @close="isAddressLookupOpen = false" />
</template>

<script>
	import api from "../api";
	import { resolveProjectPrefill } from "@/helpers/projectPrefill";
	import { auth } from "@/auth";
	import "../helpers/emoji";
	import constants from "../api/constants";
	import ProjectAutoComplete from "@/components/formfields/ProjectAutoComplete.vue";
	import SessionTagPicker from "@/components/tags/SessionTagPicker.vue";
	import timezones from "@/helpers/timezones";
	import { getDateTokens } from "@/helpers/dateFormat";
	import DeviceUtility from "@/helpers/device";
	import { isProjectFlow, navigateAfterActionStarted, returnToProjectAfterActionStarted } from "@/helpers/projectFlowReturn";
	import EventBus from "@/helpers/eventBus";
	import { PhotoPreScreenBody, PhotoPreScreenHead, writePreScreen } from "../helpers/string";
	import { closeActionDialog } from "@/helpers/closeActionDialogs";
	import CustomerAutoComplete from "@/components/formfields/CustomerAutoComplete.vue";
	import { getMessageFromError, calculateTimeout } from "@/helpers/utilities";
	import AddressLookup from "./location/AddressLookup.vue";
	import AddressLookupLink from "./location/AddressLookupLink.vue";
	import SmartPackSelector from "./scribe/SmartPackSelector.vue";
	import InstructionPicker from "./InstructionPicker.vue";
	import ScheduledSparkNotice from "./scribe/ScheduledSparkNotice.vue";
	import { buildAdminAsyncLaunchUrl } from "@/helpers/sparkyAuthHandoff";
	import { getVueTelProps } from "@/helpers/phone";
	import { useToast } from "vue-toastification";
	import {
		useInstructionCreateReturn,
		isValidScribePackSelectionDraft,
		normalizeScribePackSelectionDraft
	} from "@/composables/useInstructionCreateReturn";

	const DEFAULT_PHOTO_DRAFT_KEY = "contact:photo:create-instruction-draft";

	export default {
		components: {
			ProjectAutoComplete,
			SessionTagPicker,
			CustomerAutoComplete,
			AddressLookup,
			AddressLookupLink,
			SmartPackSelector,
			ScheduledSparkNotice,
			InstructionPicker
		},
		emits: ["close-menu"],
		mixins: [closeActionDialog],
		setup(props) {
			const toast = useToast();
			const instructionReturn = useInstructionCreateReturn({
				defaultDraftKey: DEFAULT_PHOTO_DRAFT_KEY,
				getDraftKey: () => props.instructionCreateDraftKey,
				label: "Take Photos"
			});
			return { toast, instructionReturn };
		},
		props: {
			customerId: String,
			actionText: String,
			actioningText: String,
			allowJoin: Boolean,
			buttonIcon: String,
			showHeader: Boolean,
			projectId: String,
			project: {
				type: Object,
				required: false
			},
			closeMenu: Function,
			existingConversationId: String,
			cameraUrl: String,
			projectChecklistItemId: String,
			// LSP-16289: create-and-return detour context, forwarded by ActionDialog to the Take Photos tab.
			initialInstructionId: {
				type: String,
				default: null
			},
			instructionCreateReturnTo: {
				type: String,
				default: null
			},
			instructionCreateDraftKey: {
				type: String,
				default: null
			}
		},
		data() {
			return {
				isAddressLookupOpen: false,
				showPhoneFieldErrorState: false,
				autocomplete: Math.random().toString(),
				form: false,
				currentTab: null,
				vueTelProps: getVueTelProps(),
				starting: false,
				validPhone: false,
				autoSetName: false,
				autoSetNameValue: "",
				emailDisabled: true,
				email: "",
				phone: "",
				phoneSearch: "",
				name: "",
				contactName: "",
				includeEmailInvitation: false,
				meetingUrl: "",
				actioningTextOverride: "",
				organization: {},
				autoStartRecordings: true,
				notificationTemplate: "",
				defaultNotificationTemplate: "",
				shareUser: null,
				me: null,
				selectedCustomer: null,
				validCustomer: true,
				searchCustomers: null,
				phoneSearchResultsOpen: false,
				customerJustSelected: false,
				newSelectedProject: null,
				// LSP-20406: tags applied to the session at creation.
				selectedSessionTags: [],
				projectSelectItems: [],
				isCameraOpen: false,
				capturedImage: null,
				cameraWindow: null,
				stream: null,
				conversationId: null,
				// The project the created conversation actually belongs to, recorded WITH the id so the two
				// travel together. `conversationId` outlives a single start() call, its owner did not, so any
				// later hand-off (start() re-entered, the template Join, a repeated hand-off) re-read the
				// picker and paired an existing conversation with whatever project was selected by then
				// (break-it round 2). null means "no owner recorded" -- an existing conversation adopted from
				// a prop, where the live read is still the best available answer. "" means "created with no
				// project", which must NOT fall through to one.
				conversationProjectId: null,
				creatingConversation: false,
				title: "",
				cachedUser: {},
				placeholderTitle: "",
				disableProjectField: false,
				overridePopupUrl: "",
				scribePackSelection: null,
				scribePackSelectionTouched: false,
				scribePackProcessing: false,
				scribeUserInputPending: false,
				instructionId: null,
				// LSP-20840: preselecting the org default is allowed on a fresh open, or after the
				// LSP-16289 restore settles WITHOUT consuming a draft/returned id — a consumed draft
				// (even one whose instructionId is an explicit clear) always wins over the default.
				instructionPreselectAllowed: false
			};
		},
		computed: {
			contentNotificationDelay() {
				return this.me?.config?.contentNotificationDelaySeconds;
			},
			isSubpageOpen() {
				return this.isAddressLookupOpen;
			},
			dynamicActioningText() {
				if (this.actioningTextOverride) {
					return this.actioningTextOverride;
				}
				return "Take Photos";
			},
			computedTitle() {
				if (this.me?.user) {
					return `Photos from ${this.getTeamMemberName()} on ${timezones.formatDate(new Date().toISOString(), getDateTokens(this.organization?.dateFormat).dateShortMonth)}`;
				} else {
					return `Photos taken on ${timezones.formatDate(new Date().toISOString(), getDateTokens(this.organization?.dateFormat).dateShortMonth)}`;
				}
			}
		},
		async mounted() {
			const returnedInstruction =
				(this.$route.path || "").replace(/\/+$/, "").toLowerCase() === "/photo"
					? this.instructionReturn.consumeReturnQuery(this.$route, this.$router, "photo")
					: null;
			// make sure this goes first
			this.reset();
			this.scribePackSelectionTouched = false;
			this.tryOpenAddressLookup();
			this.me = await api.getMe();
			// Must precede reading computedTitle: it formats the name date via
			// this.organization.dateFormat (LSP-15050).
			this.organization = this.me.organization;

			this.title = this.computedTitle;
			this.placeholderTitle = this.title;

			if (this.project) {
				this.newSelectedProject = this.project;
			} else if (this.projectId) {
				const result = await api.getProject(this.projectId);
				this.newSelectedProject = result;
			}

			if (this.projectChecklistItemId || this.projectId || this.project) {
				this.disableProjectField = true;
			}

			// LSP-16205: when opened from a project, default the contact to the project's
			// first assigned contact so the user isn't forced to re-search. An explicit
			// customerId prop still takes precedence, and the prefilled value stays editable.
			const { project: resolvedProject, customerId: prefillCustomerId } = await resolveProjectPrefill({
				customerId: this.customerId,
				projectId: this.projectId,
				project: this.newSelectedProject ?? this.project
			});
			if (resolvedProject) {
				this.newSelectedProject = resolvedProject;
			}
			if (prefillCustomerId) {
				try {
					const result = await api.getCustomers({ where: { id: prefillCustomerId } });
					if (result.count > 0) {
						this.selectedCustomer = result.rows[0];
					}
				} catch (error) {
					// A failed prefill is non-critical — degrade to "no contact prefilled" rather than
					// aborting the rest of mounted() (organization/setup below run on this same path).
					console.warn(`[LSP-16205] contact prefill lookup failed for customer ${prefillCustomerId}`, error);
				}
			}

			if (this.existingConversationId) {
				this.conversationId = this.existingConversationId;
				await this.start();
			}
			// check if there is a overridePopupUrl in the query params
			const params = new URLSearchParams(window.location.search);
			const overridePopupUrl = params.get("overridePopupUrl");
			if (overridePopupUrl) {
				this.overridePopupUrl = overridePopupUrl;
			}

			// LSP-16289: restore the in-progress draft only when a return-handoff signal is present.
			// The auto-submit path (existingConversationId) never carries detour props, so this gate
			// stays inert there.
			if (returnedInstruction || this.instructionCreateDraftKey !== null || this.initialInstructionId !== null) {
				await this.restoreInstructionCreateDraft();
			} else {
				// LSP-20840: fresh open with no handoff context at all — the default may preselect.
				this.instructionPreselectAllowed = true;
			}
		},
		beforeUnmount() {},
		methods: {
			updateScribePackProcessing(processing) {
				this.scribePackProcessing = processing;
				if (processing) this.scribePackSelectionTouched = true;
			},
			tryOpenAddressLookup() {
				const params = new URLSearchParams(window.location.search);
				const goTo = params.get("goTo");
				if (goTo === "address") {
					this.openAddressLookup();
				}
			},
			openAddressLookup() {
				this.isAddressLookupOpen = true;
			},
			// TODO: Consolidate this into a helper or single Action dialog component
			getTeamMemberName() {
				let name = null;

				if (this.me?.user.firstname) {
					name = this.me?.user.firstname;
				}

				if (this.me?.user.lastname) {
					name = `${name} ${this.me?.user.lastname}`;
				}

				if (!name) {
					name = this.me?.user.email;
				}

				return name ?? "-";
			},
			async updateProject(project) {
				this.isAddressLookupOpen = false;
				this.newSelectedProject = project;
			},
			titleChanged() {
				if (!this.title || this.title == "") {
					this.title = this.computedTitle;
				}
			},
			cardClass() {
				return `pa-1 conversation-card ${DeviceUtility.isMobile ? "conversation-bottom" : ""}`;
			},
			startButtonDisabled() {
				return this.starting || !!this.actioningTextOverride || this.scribePackProcessing;
			},
			reset() {
				this.$refs.smartPackSelector?.reset?.();
				this.scribePackSelectionTouched = false;
				this.isAddressLookupOpen = false;
				this.phone = "";
				this.email = "";
				this.includeEmailInvitation = false;
				this.name = "";
				this.autoSetName = "";
				this.autoSetNameValue = "";
				this.shareUser = null;
				this.selectedCustomer = null;
				this.scribePackSelection = null;
				this.selectedSessionTags = [];
				this.instructionId = null;
				this.scribePackProcessing = false;
				this.scribeUserInputPending = false;
			},
			customerSelected(customer) {
				if (customer) {
					this.customerJustSelected = true;
				}

				this.setCustomer(customer);
			},
			async start() {
				if (this.startButtonDisabled()) {
					return;
				}
				if (!this.conversationId) {
					if (!this.form || !this.title || this.title.trim() === "") {
						await this.$refs.form.validate();
						return;
					}
					if (this.projectId && !this.newSelectedProject) {
						return;
					}
					if (this.scribeUserInputPending) {
						return;
					}
				}
				// The project the user is returned to must be the project the conversation belongs to,
				// which is fixed when the conversation is created — not re-read after the popup, token,
				// and create awaits below (PR #3891 review, mirroring ActionUploadPhoto's snapshot).
				// Declared out here because the hand-off that consumes it sits outside the create block;
				// this initial value covers the already-have-a-conversation path, which never creates.
				// ls-heroku-ci (HIGH): ONE identity for the whole action, captured before ANY await, the same
				// principle ActionUploadPhoto.save() applies. The contact back-fill below runs after getMe (and
				// after the popup/pre-screen waits) and read the project and contact LIVE, so a contact or project
				// switched while those awaits were in flight wrote the WRONG contact into the WRONG project's
				// Customers list. That is a persistent api.updateProject write, not a display glitch.
				//
				// Used for the create request too, deliberately. Snapshotting only the back-fill would leave the
				// request reading live and the two would then disagree about which project the action was for --
				// the half-applied-snapshot mistake this PR already had to correct once in ActionUploadPhoto,
				// where it wrote one customer onto the project and a different one onto the conversation.
				//
				// References, not copies: the hazard is the picker EMITTING a different object, which capturing the
				// reference covers, and it leaves the in-place Customers mutation below behaving exactly as before.
				const actionProject = this.newSelectedProject;
				const actionContact = this.selectedCustomer;
				// Captured with the contact they belong to: the create request reads all three together.
				const actionPhone = (this.phone || "").trim();
				const actionEmail = (this.email || "").trim();
				const actionScribePackSelection = this.scribePackSelection;
				// LSP-20406: captured with the other send inputs so a selection edit during the awaited
				// create cannot change which tags the request carries.
				const actionSessionTags = [...this.selectedSessionTags];
				let projectIdForReturn = this.conversationProjectId ?? this.newSelectedProject?.id ?? this.projectId;
				let win = null;
				// LSP-15388 review (CRITICAL): once the popup has been pointed at the capture url the
				// user's session is LIVE. Nothing after that point may reach the outer catch's
				// popup teardown below, which would destroy a capture that started successfully.
				let captureStarted = false;
				// SINGLE path for tearing the popup down. Review follow-up: the guard was applied to the
				// first close in the catch but not the second, so the code logged "leaving the live popup
				// open" and then closed it twenty lines later — a diagnostic asserting the opposite of what
				// happened. Routing every teardown through one function means a future close cannot be
				// added without inheriting the guard.
				const closePopupUnlessCaptureStarted = (phase) => {
					if (captureStarted) {
						console.warn("ActionPhoto.start: refusing to close the popup — the capture is live", {
							tags: { method: "start", file: "ActionPhoto", phase },
							extra: { conversationId: this.conversationId }
						});
						return;
					}
					if (!win) {
						return;
					}
					try {
						win.close();
					} catch (closeError) {
						console.error("Unable to close popup", closeError);
					}
				};
				let popupOpened = false;
				let cameraUrl = null;
				// LSP-15319: gate on the PWA itself, not on Android. isPwaInstalled is
				// matchMedia("(display-mode: standalone)"), which an iOS home-screen app reports
				// too — so the old `isAndroid &&` conjunct sent installed iOS PWAs down the popup
				// path, where there are no browser tabs to open into. That either handed the
				// capture off to Safari or returned null and raised a popup-blocker toast the user
				// could do nothing about. Every installed PWA now takes the current-tab fallback.
				const installedPwa = DeviceUtility.isPwaInstalled;

				this.starting = true;

				try {
					if (!installedPwa) {
						win = window.open(this.overridePopupUrl || "", "_blank");
						if (!win) {
							this.toast.error(
								"Unable to open a new window. Please check your popup blocker settings or close some tabs and try again.",
								{
									timeout: calculateTimeout(
										"Unable to open a new window. Please check your popup blocker settings or close some tabs and try again."
									)
								}
							);
							return;
						}
						if (this.overridePopupUrl) {
							//sleep for 1 second
							await new Promise((resolve) => setTimeout(resolve, 1000));
						}
					}

					try {
						// accessing the popup window can throw an exception in certain browsers in some unknown conditions
						// this has been observed in Safari
						if (win && win.document && win.document.body) {
							writePreScreen(win, PhotoPreScreenHead, PhotoPreScreenBody(window.location.origin));
							popupOpened = true;
						}
					} catch (error) {
						// log and continue as we have fallbacks in place
						console.warn("Unable to set popup content. Will use window redirect instead.", error);
					}

					// open camera
					cameraUrl = this.cameraUrl;
					if (!this.conversationId) {
						this.creatingConversation = true;
						const me = await api.getMe();
						if (actionProject?.id && actionContact?.id) {
							let contactExistance = false;
							if (!actionProject.Customers) {
								actionProject.Customers = [];
							}
							for (const contact of actionProject.Customers) {
								if (contact.id === actionContact.id) {
									contactExistance = true;
									break;
								}
							}
							if (!contactExistance) {
								await api.updateProject(actionProject.id, {
									Customers: [...actionProject.Customers, actionContact]
								});
							}
						}
						const request = {
							forcedUserId: me.user.id,
							name: this.title ?? "Photos Session",
							type: constants.CONVERSATION_TYPE.PHOTOS,
							initiationType: constants.INITIATION_TYPE.ADMIN,
							projectId: actionProject?.id,
							projectChecklistItemId: this.projectChecklistItemId,
							scribePackId: actionScribePackSelection?.pack?.id ?? undefined,
							scribePackName: actionScribePackSelection?.pack?.name ?? undefined,
							scribePackTaskIds: actionScribePackSelection?.taskIds ?? undefined,
							scribePackUserInputData: actionScribePackSelection?.userInputData ?? undefined,
							instructionId: this.instructionId ?? undefined,
							// LSP-20406: attach the picked tags atomically at creation (server upsertRelatedLabels).
							Labels: actionSessionTags.length ? actionSessionTags : undefined
						};

						// The SAME snapshot the back-fill above uses, for the reason ActionUploadPhoto already had to
						// be corrected on: one action must not write one customer onto the project and a different one
						// onto its conversation.
						if (actionContact?.id) {
							request.visitors = [
								{
									customerId: actionContact.id,
									phone: actionPhone,
									email: actionEmail,
									firstname: actionContact.firstname,
									lastname: actionContact.lastname,
									notificationSMS: false,
									notificationEmail: false
								}
							];
						}

						// Pinned to the projectId this request actually carries, so the return target is the
						// project that owns the new conversation even if the selection changes during the await.
						// Empty string, not undefined: "the request carried no project" has to be
						// distinguishable from "nobody supplied a value", or the ?? fallbacks downstream
						// re-invent this.projectId and return the user to a project the new conversation is
						// not in (break-it round 1). returnToProjectAfterActionStarted refuses an empty id
						// and logs the route context, so the refusal is diagnosable rather than silent.
						projectIdForReturn = request.projectId ?? "";
						// (recorded onto conversationProjectId beside conversationId below)
						const resp = await api.createConversation(request);
						const conversation = resp.conversation;
						this.conversationId = conversation.id;
						this.conversationProjectId = projectIdForReturn;
						this.creatingConversation = false;
						cameraUrl = resp.url;
					}
					cameraUrl = await this.appendAdminAsyncSparkyAuthToken(cameraUrl);

					if (popupOpened) {
						win.location = cameraUrl;
						// POINT OF NO RETURN — the capture is now live in the popup.
						captureStarted = true;
						window.addEventListener("message", this.handleCameraMessage);

						// Everything below is navigation/notification only. It is locally try/caught so a
						// throw — most plausibly a parent's synchronous close-menu handler, or a routing
						// failure — cannot propagate to the outer catch, which would close the popup the
						// user is actively capturing into and toast an error for what was a success.
						// Mirrors the guard ActionVideo.join already applies at its own popup hand-off.
						try {
							// LSP-15388: if the user is not in a project flow, send them to the conversation
							// that was just created. In a project flow the desktop dialog sits over a mounted
							// project page, so close-menu is all that is needed — but on mobile the FAB routed
							// here as a standalone /photo page, and only navigating back re-mounts the project.
							if (!isProjectFlow(this.$route, "ActionPhoto")) {
								navigateAfterActionStarted({
									router: this.$router,
									target: {
										name: "conversation",
										params: {
											id: this.conversationId
										},
										query: {
											from: "homepage"
										}
									},
									componentName: "ActionPhoto",
									context: { conversationId: this.conversationId },
									destination: "to the conversation"
								});
							} else {
								// ls-heroku-ci: the emit gets its OWN try, matching ActionVideo and ActionUploadPhoto. Sharing
								// one try with the navigate call below let a throwing close-menu listener skip the navigation
								// entirely and land in the outer catch -- the user stays put with only a warning. Not
								// exploitable with today's listener wiring, but that is a coincidence of which components
								// happen to be mounted, not a guarantee.
								try {
									this.$emit("close-menu", this.conversationId);
								} catch (error) {
									console.warn("ActionPhoto.start: a close-menu consumer failed, continuing to navigate", {
										tags: { method: "start", file: "ActionPhoto" },
										extra: { conversationId: this.conversationId, error }
									});
								}
								returnToProjectAfterActionStarted({
									route: this.$route,
									router: this.$router,
									projectId: projectIdForReturn,
									componentName: "ActionPhoto"
								});
							}
						} catch (navigationError) {
							console.warn("ActionPhoto.start: post-capture hand-off failed; the capture itself is unaffected", {
								tags: { method: "start", file: "ActionPhoto", phase: "post-capture-handoff" },
								extra: { conversationId: this.conversationId, error: navigationError }
							});
						}
					} else {
						if (installedPwa) {
							console.log("Installed PWA. Redirecting current tab", {
								isAndroid: DeviceUtility.isAndroid,
								isIOS: DeviceUtility.isIOS
							});
						} else {
							console.error("Unable to open popup. Redirecting in current tab.");
						}
						window.location = cameraUrl;
					}
				} catch (e) {
					const message = getMessageFromError(e);
					this.toast.error(message, {
						timeout: calculateTimeout(message)
					});

					closePopupUnlessCaptureStarted("post-capture-error");

					console.error("Unable to to start photo session", e);

					console.error(e, {
						tags: {
							method: "start",
							file: "ActionPhoto"
						},
						extra: {
							type: constants.CONVERSATION_TYPE.PHOTOS
						}
					});

					closePopupUnlessCaptureStarted("catch-cleanup");

					// If all else fails. Also gated: once the capture is live in the popup, navigating THIS
					// tab to the camera url starts a second session rather than recovering the first.
					if (cameraUrl && !captureStarted) {
						console.error("Unable to start photo session in popup. Redirecting in current tab.");
						window.location = cameraUrl;
						return;
					}
				} finally {
					this.starting = false;
				}
			},
			setCustomer(customer) {
				this.selectedCustomer = customer;
				if (customer) {
					if (customer.EmailAddresses?.length > 0) {
						this.email = customer.EmailAddresses[0].emailAddress;
						this.emailDisabled = true;
					} else {
						this.emailDisabled = false;
					}

					if (!this.phone && customer.PhoneNumbers?.length > 0) {
						this.phone = customer.PhoneNumbers[0].phoneNumber;
					}
				} else {
					this.emailDisabled = false;
					this.name = "";
					this.email = "";
				}
			},
			handleCameraMessage(event) {
				if (event.data.type === "IMAGE_CAPTURED") {
					console.debug("Image captured");
					EventBus.$emit("ReloadPhotos", this.conversationId);
					EventBus.$emit("ReloadPhotos", this.newSelectedProject?.id);
				}
			},
			// LSP-21256: also carries the dashboard launch surface to the recorder (buildAdminAsyncLaunchUrl).
			async appendAdminAsyncSparkyAuthToken(url) {
				return buildAdminAsyncLaunchUrl(url, auth);
			},
			// LSP-16289: persist the in-progress Take Photos fields before the same-tab navigation to
			// the instruction editor. Only the minimal serializable shape is kept.
			saveInstructionCreateDraft() {
				this.instructionReturn.persistDraft({
					title: this.title,
					// Take Photos' only contact representation is the selected customer object (there is no
					// phone/name text field), so persist its id to re-fetch on return — otherwise reset()
					// clears it and the chosen contact is silently dropped after the editor detour.
					selectedCustomerId: this.selectedCustomer?.id ?? null,
					selectedProjectId: this.newSelectedProject?.id ?? null,
					scribePackSelection: this.scribePackSelection ? normalizeScribePackSelectionDraft(this.scribePackSelection) : null,
					scribeUserInputPending: this.scribeUserInputPending,
					instructionId: this.instructionId
				});
			},
			// Restore the draft saved by saveInstructionCreateDraft(). A returned `initialInstructionId`
			// takes precedence over the draft's (now stale) instructionId.
			async restoreInstructionCreateDraft() {
				const draft = this.instructionReturn.loadRawDraft();
				// LSP-20840: nothing to restore (no draft, no returned id) → the default may preselect.
				this.instructionPreselectAllowed = !draft && this.initialInstructionId === null;
				if (draft) {
					if (typeof draft.title === "string") {
						this.title = draft.title;
					}
					if (
						!this.scribePackSelectionTouched &&
						Object.prototype.hasOwnProperty.call(draft, "scribePackSelection") &&
						(draft.scribePackSelection === null || isValidScribePackSelectionDraft(draft.scribePackSelection))
					) {
						this.scribePackSelection =
							draft.scribePackSelection === null ? null : normalizeScribePackSelectionDraft(draft.scribePackSelection);
						if (typeof draft.scribeUserInputPending === "boolean") {
							this.scribeUserInputPending = draft.scribeUserInputPending;
						}
					}
					if (typeof draft.selectedProjectId === "string" && draft.selectedProjectId) {
						try {
							this.newSelectedProject = await api.getProject(draft.selectedProjectId);
						} catch (error) {
							console.warn("Unable to restore the selected project from the Take Photos draft", error);
						}
					}
					if (typeof draft.selectedCustomerId === "string" && draft.selectedCustomerId) {
						try {
							const result = await api.getCustomers({ where: { id: draft.selectedCustomerId } });
							if (result.count > 0) {
								// Route through setCustomer (not a direct assign) so email/phone are synced from
								// the contact exactly as when the user first picked it — reset() cleared them on
								// mount, and Take Photos does not persist them separately, so start() would
								// otherwise submit a visitor with blank phone/email despite a restored contact.
								this.setCustomer(result.rows[0]);
							} else {
								// Silent-failure rule: the contact was deleted/merged during the detour, so it
								// cannot be re-selected. Leave a breadcrumb so a half-restored dialog is traceable.
								console.warn("Take Photos draft: selected contact no longer resolves on restore", {
									selectedCustomerId: draft.selectedCustomerId
								});
							}
						} catch (error) {
							console.warn("Unable to restore the selected contact from the Take Photos draft", error);
						}
					} else if (draft.selectedCustomerId === null) {
						// The user explicitly cleared the contact before the detour. Clear it (setCustomer(null)
						// also resets name/email) so a mounted() project prefill cannot reintroduce a contact
						// the user removed and make start() associate the wrong customer.
						this.setCustomer(null);
					}
				}
				this.instructionId = this.instructionReturn.resolveInstructionId(
					draft?.instructionId,
					this.instructionReturn.returnedInstructionId(this.initialInstructionId),
					this.instructionId
				);
				this.instructionReturn.clearDraft();
			}
		}
	};
</script>

<style lang="scss" scoped>
	.recording-disclaimer {
		color: #5e5c6e;
		font-size: 0.9em;
		text-align: center;
		padding: 24px;
	}
	.start-conversation-button {
		font-family: "Inter Tight", sans-serif;
		font-size: 20px;
		color: white;
		margin-left: 10px;
	}
	.reset-span {
		position: relative;
		left: calc(100% - 50px);
		display: inline-block;
		top: -30px;
		height: 0;
	}

	.conversation-card {
		border-radius: 12px;
		min-height: 400px;
		display: flex;
		flex-direction: column;
		justify-content: start;
	}

	.conversation-card .header {
		display: flex;
		margin-bottom: 28px;
		margin-top: 12px;
	}

	.conversation-card textarea {
		color: #212121;
		font-size: 16px;
	}

	.conversation-card h2,
	.conversation-card i {
		font-size: 24px;
		margin-right: 5px;
	}

	.conversation-card .header {
		color: #000;
	}

	.conversation-card .auto-message {
		color: #b3b3b3;
		font-size: 16px;
	}

	.conversation-card .record-switch {
		margin-left: 5px;
		margin-top: 0px;
	}

	.conversation-card .contact-name.reset {
		padding-top: 0px;
	}

	.field-border-radius {
		border-radius: 6px;
	}

	.v-slide-group__next,
	.v-slide-group__prev {
		display: none;
	}

	/* Email Invite */

	.team-member-share {
		position: absolute;
		padding-top: 8px;
		margin-left: 2px;
		color: rgb(var(--v-theme-brand));
	}

	.customer-phone-search {
		position: absolute;
		top: 0;
		left: 0;
		visibility: hidden;
	}
</style>
